import {
  Glass,
  ScreenLabel,
  DisplayText,
  BodyText,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";
import { DataNeedIcon } from "./journey-shared";

function CheckItem({ type, label, status, action, accent }) {
  const isReady = status === "ready";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 0",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      {/* Status icon */}
      {isReady ? (
        <svg width="17" height="17" viewBox="0 0 17 17" style={{ flexShrink: 0 }}>
          <circle cx="8.5" cy="8.5" r="8.5" fill="var(--color-kin-green-soft)" />
          <path
            d="M5 8.5 L7.3 10.8 L12 6.2"
            stroke="#fff"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <div
          style={{
            width: 17,
            height: 17,
            borderRadius: "50%",
            background: alpha("var(--color-kin-orange)", "12"),
            border: "2px solid var(--color-kin-orange)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "var(--color-kin-orange)",
            }}
          />
        </div>
      )}

      <DataNeedIcon type={type} accent={isReady ? "var(--color-kin-green-soft)" : accent} />

      <div style={{ flex: 1 }}>
        <BodyText
          size={10}
          weight={600}
          color={isReady ? "var(--color-kin-black)" : "var(--color-kin-black)"}
        >
          {label}
        </BodyText>
        <BodyText
          size={8}
          color={isReady ? "var(--color-kin-green-soft)" : "var(--color-kin-orange)"}
          weight={500}
        >
          {isReady ? "In your vault" : "Missing"}
        </BodyText>
      </div>

      {/* Action pill for missing items */}
      {!isReady && action && (
        <div
          style={{
            padding: "4px 10px",
            borderRadius: 20,
            background: alpha(accent, "14"),
            border: borderAlpha(accent, "30"),
          }}
        >
          <BodyText size={8} color={accent} weight={700}>
            {action}
          </BodyText>
        </div>
      )}
    </div>
  );
}

export function JourneyInstallDataCheckScreen() {
  const accent = "var(--color-kin-pink)";
  const readyCount = 3;
  const totalCount = 5;

  const items = [
    { type: "wearable", label: "Heart rate data", status: "ready" },
    { type: "workout", label: "Workout history", status: "ready" },
    { type: "sleep", label: "Sleep data", status: "ready" },
    { type: "wearable", label: "Garmin Connect", status: "missing", action: "Connect" },
    { type: "lab", label: "Lipid panel", status: "missing", action: "Upload PDF" },
  ];

  return (
    <div
      style={{
        padding: "14px 16px 0",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        height: "100%",
        position: "relative",
      }}
    >
      {/* Header */}
      <div>
        <ScreenLabel style={{ marginBottom: 4, display: "block" }}>Setting up</ScreenLabel>
        <DisplayText size={20}>Cardio Fitness</DisplayText>
        <BodyText
          size={9.5}
          color="var(--color-kin-muted)"
          weight={400}
          style={{ marginTop: 4, lineHeight: 1.45 }}
        >
          Check which data you already have and what's missing for the best results.
        </BodyText>
      </div>

      {/* Progress fraction */}
      <Glass style={{ padding: "12px 14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <BodyText size={11} weight={700} color="var(--color-kin-black)">
            {readyCount} of {totalCount} data sources ready
          </BodyText>
          <BodyText size={9} color="var(--color-kin-green-soft)" weight={700}>
            {Math.round((readyCount / totalCount) * 100)}%
          </BodyText>
        </div>
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: "rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${(readyCount / totalCount) * 100}%`,
              height: "100%",
              borderRadius: 3,
              background:
                "linear-gradient(90deg, var(--color-kin-green-soft), var(--color-kin-green-mid))",
            }}
          />
        </div>
      </Glass>

      {/* Checklist */}
      <div>
        <ScreenLabel style={{ marginBottom: 6, display: "block" }}>Data sources</ScreenLabel>
        <Glass style={{ padding: "0 12px" }}>
          {items.map((item) => (
            <CheckItem key={`${item.type}-${item.label}`} {...item} accent={accent} />
          ))}
        </Glass>
      </div>

      <BodyText size={8.5} color="var(--color-kin-muted)" weight={500} style={{ lineHeight: 1.45 }}>
        Minimum 2 required sources connected. Optional sources improve accuracy.
      </BodyText>

      {/* Continue CTA */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "8px 16px 12px",
          background: "linear-gradient(to top, rgba(248,245,238,1) 60%, rgba(248,245,238,0))",
          zIndex: 5,
        }}
      >
        <div
          style={{
            background: "var(--color-kin-lavender)",
            borderRadius: 14,
            padding: "12px 0",
            textAlign: "center",
            boxShadow: "0 4px 16px -4px rgba(165,144,255,0.4)",
          }}
        >
          <BodyText size={11} color="#fff" weight={700}>
            Continue
          </BodyText>
        </div>
      </div>
    </div>
  );
}
